import type { CSSProperties } from "react"

import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarProvider,
  SidebarRail,
} from "@/components/ui/sidebar"
import { TooltipProvider } from "@/components/ui/tooltip"

interface OperatorShellSkeletonProps {
  contentCards?: number
}

export function OperatorShellSkeleton({
  contentCards = 3,
}: OperatorShellSkeletonProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <SidebarProvider
        defaultOpen
        style={
          {
            "--sidebar-width": "18rem",
            "--sidebar-width-mobile": "18rem",
          } as CSSProperties
        }
      >
        <Sidebar collapsible="icon" variant="inset">
          <SidebarHeader className="gap-3 p-3">
            <div className="flex items-center gap-3 rounded-xl border border-sidebar-border/70 bg-sidebar/60 px-3 py-3">
              <Skeleton className="size-10 rounded-xl bg-sidebar-accent" />
              <div className="grid min-w-0 flex-1 gap-2">
                <Skeleton className="h-4 w-28 bg-sidebar-accent" />
                <Skeleton className="h-3 w-36 bg-sidebar-accent/80" />
              </div>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Operator surfaces</SidebarGroupLabel>
              <SidebarMenu>
                {Array.from({ length: 5 }).map((_, index) => (
                  <SidebarMenuItem key={`primary-${index}`}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
            <SidebarGroup className="mt-auto pt-0">
              <SidebarGroupLabel>Shortcuts</SidebarGroupLabel>
              <SidebarMenu>
                {Array.from({ length: 2 }).map((_, index) => (
                  <SidebarMenuItem key={`secondary-${index}`}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="p-3">
            <div className="flex items-center gap-3 rounded-xl border border-sidebar-border/70 bg-sidebar/60 px-3 py-3">
              <Skeleton className="size-10 rounded-xl bg-sidebar-accent" />
              <div className="grid min-w-0 flex-1 gap-2">
                <Skeleton className="h-4 w-24 bg-sidebar-accent" />
                <Skeleton className="h-3 w-32 bg-sidebar-accent/80" />
                <Skeleton className="h-3 w-20 bg-sidebar-accent/70" />
              </div>
            </div>
          </SidebarFooter>
          <SidebarRail />
        </Sidebar>
        <SidebarInset className="min-h-svh bg-transparent">
          <header className="border-b border-border/60 bg-background/82">
            <div className="mx-auto flex min-h-16 w-full max-w-7xl items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
              <Skeleton className="size-8 rounded-md" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-40" />
            </div>
          </header>
          <div className="px-4 py-5 sm:px-6 lg:px-8">
            <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
              <section className="flex flex-col gap-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-8 w-full max-w-sm" />
                <Skeleton className="h-4 w-full max-w-2xl" />
              </section>
              <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {Array.from({ length: contentCards }).map((_, index) => (
                  <Card
                    key={`content-card-${index}`}
                    className="shell-panel rounded-4xl border-border/70"
                  >
                    <CardHeader className="gap-3">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-4 w-full max-w-xs" />
                    </CardHeader>
                    <CardContent className="flex flex-col gap-3">
                      <Skeleton className="h-12 w-full rounded-xl" />
                      <Skeleton className="h-12 w-full rounded-xl" />
                      <Skeleton className="h-12 w-4/5 rounded-xl" />
                    </CardContent>
                  </Card>
                ))}
              </section>
              <Card className="shell-panel rounded-4xl border-border/70">
                <CardHeader className="gap-3">
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-full max-w-lg" />
                </CardHeader>
                <CardContent className="grid gap-3">
                  {Array.from({ length: 5 }).map((_, index) => (
                    <Skeleton
                      key={`row-${index}`}
                      className="h-11 w-full rounded-xl"
                    />
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
